import { emptyJobRoleContent, type JobRoleContent, type JobRoleTemplateDetail } from './types';

/**
 * The ten synthetic EPN job role templates the mock serves (the real ones are seeded server-side from SeedData/job-role-templates.json).
 * The wording is written generically for this mock and is not copied from O*NET or any third party. They carry no pay figure of
 * any kind: `typicalPayType` is only the usual way the role is paid, never a rate.
 */


interface Seed {
  slug: string;
  code: string;
  title: string;
  socCode: string | null;
  jobFamily: string;
  careerLevel: string;
  department: string;
  purpose: string;
  summary: string;
  duties: string[];
  responsibilities: string[];
  outcomes: string[];
  reportsTo: string;
  education: string;
  experience: string;
  skills: string[];
  professionalSkills: string[];
  /** Free text, like the real seed ("Hourly", "Salary", "Salary and commission"). */
  payType: string;
  overtimeEligible: boolean | null;
  commission?: boolean;
  bonus?: boolean;
  flsa?: string;
}

function build(seed: Seed): JobRoleContent {
  const base = emptyJobRoleContent();
  return {
    ...base,
    purpose: seed.purpose,
    summary: seed.summary,
    alternateTitles: [],
    essentialDuties: seed.duties,
    responsibilities: seed.responsibilities,
    expectedOutcomes: seed.outcomes,
    decisionAuthority: 'Makes day-to-day decisions within company policy and escalates exceptions to the manager.',
    reportsTo: seed.reportsTo,
    qualifications: {
      ...base.qualifications,
      minimumEducation: seed.education,
      minimumExperience: seed.experience,
      technicalSkills: seed.skills,
      professionalSkills: seed.professionalSkills,
      workArrangement: 'On-site or hybrid, as set by the employer',
    },
    employmentGuidance: {
      ...base.employmentGuidance,
      typicalEmploymentType: 'Employee',
      typicalPayType: seed.payType,
      hourlyOrSalaried: seed.payType === 'Hourly' ? 'Hourly' : 'Salaried',
      fullOrPartTime: 'Full time',
      flsaClassification: seed.flsa ?? null,
      overtimeEligible: seed.overtimeEligible,
      commissionEligible: seed.commission ?? false,
      bonusEligible: seed.bonus ?? false,
      benefitEligibilityGuidance: 'Follows the company benefits policy for the employee’s employment type.',
    },
  };
}

const SEEDS: Seed[] = [
  {
    slug: 'payroll-specialist', code: 'PAY-SPEC', title: 'Payroll Specialist', socCode: '43-3051', jobFamily: 'Finance and Accounting',
    careerLevel: 'Intermediate', department: 'Finance',
    purpose: 'Keeps each payroll cycle accurate, on time and well documented.',
    summary: 'Prepares and reviews payroll inputs, answers employee pay questions and supports payroll close.',
    duties: ['Collect and check time, leave and pay changes before each payroll', 'Review payroll registers and investigate differences', 'Answer employee questions about their pay'],
    responsibilities: ['Maintain payroll records and supporting documents', 'Support audits and period-end reconciliations'],
    outcomes: ['Payroll is submitted for approval on schedule', 'Corrections are rare and explained'],
    reportsTo: 'Payroll Manager', education: 'High school diploma or equivalent', experience: '2 years in payroll or accounting support',
    skills: ['Payroll software', 'Spreadsheets'], professionalSkills: ['Attention to detail', 'Confidentiality'],
    payType: 'Hourly', overtimeEligible: null,
  },
  {
    slug: 'senior-payroll-specialist', code: 'PAY-SR', title: 'Senior Payroll Specialist', socCode: '43-3051', jobFamily: 'Finance and Accounting',
    careerLevel: 'Senior', department: 'Finance',
    purpose: 'Leads complex payroll work and raises the quality of the payroll team.',
    summary: 'Owns multi-state and exception payroll processing and coaches other payroll specialists.',
    duties: ['Process payrolls with multiple pay schedules or locations', 'Resolve escalated pay and adjustment cases', 'Review work prepared by other payroll specialists'],
    responsibilities: ['Document payroll procedures', 'Recommend process improvements to the Payroll Manager'],
    outcomes: ['Exception cases are resolved within the agreed time', 'New specialists reach independence quickly'],
    reportsTo: 'Payroll Manager', education: 'Associate degree or equivalent experience', experience: '5 years in payroll',
    skills: ['Payroll software', 'Reconciliation', 'Spreadsheets'], professionalSkills: ['Coaching', 'Problem solving'],
    payType: 'Salary', overtimeEligible: null,
  },
  {
    slug: 'payroll-manager', code: 'PAY-MGR', title: 'Payroll Manager', socCode: '11-3031', jobFamily: 'Finance and Accounting',
    careerLevel: 'Manager', department: 'Finance',
    purpose: 'Is accountable for accurate, compliant and timely payroll.',
    summary: 'Manages the payroll team and the payroll calendar, controls and reporting.',
    duties: ['Approve payroll submissions before they are released for approval', 'Maintain payroll controls and separation of duties', 'Manage the payroll team and its workload'],
    responsibilities: ['Report payroll results to finance leadership', 'Coordinate with HR, accounting and external advisors'],
    outcomes: ['Payroll controls operate as designed', 'Audit requests are answered without rework'],
    reportsTo: 'Chief Financial Officer', education: 'Bachelor’s degree in accounting, finance or a related field, or equivalent experience',
    experience: '7 years in payroll including 2 years leading a team', skills: ['Payroll software', 'Internal controls'],
    professionalSkills: ['Leadership', 'Communication'], payType: 'Salary', overtimeEligible: null, bonus: true,
  },
  {
    slug: 'staff-accountant', code: 'ACC-STAFF', title: 'Staff Accountant', socCode: '13-2011', jobFamily: 'Finance and Accounting',
    careerLevel: 'Intermediate', department: 'Accounting',
    purpose: 'Maintains accurate books and supports the monthly close.',
    summary: 'Records transactions, prepares reconciliations and supports financial reporting.',
    duties: ['Post and reconcile journal entries', 'Prepare account reconciliations each month', 'Support the monthly and year-end close'],
    responsibilities: ['Maintain supporting schedules', 'Respond to routine finance questions'],
    outcomes: ['Reconciliations are complete at close', 'Open items are cleared promptly'],
    reportsTo: 'Controller', education: 'Bachelor’s degree in accounting or a related field', experience: '1 to 3 years in accounting',
    skills: ['General ledger software', 'Spreadsheets'], professionalSkills: ['Accuracy', 'Time management'],
    payType: 'Salary', overtimeEligible: null,
  },
  {
    slug: 'human-resources-coordinator', code: 'HR-COORD', title: 'Human Resources Coordinator', socCode: '13-1071', jobFamily: 'Human Resources',
    careerLevel: 'Entry', department: 'Human Resources',
    purpose: 'Keeps people processes organized for employees and managers.',
    summary: 'Coordinates onboarding, employee records and routine HR requests.',
    duties: ['Prepare onboarding and offboarding checklists', 'Keep employee records complete and current', 'Answer routine HR questions or route them to the right person'],
    responsibilities: ['Schedule interviews and orientation sessions', 'Support HR reporting'],
    outcomes: ['New hires have what they need on day one', 'Records are accurate when reviewed'],
    reportsTo: 'HR Manager', education: 'High school diploma or equivalent', experience: '0 to 2 years in an office or HR support role',
    skills: ['HR records systems', 'Scheduling'], professionalSkills: ['Discretion', 'Organization'],
    payType: 'Hourly', overtimeEligible: null,
  },
  {
    slug: 'operations-manager', code: 'OPS-MGR', title: 'Operations Manager', socCode: '11-1021', jobFamily: 'Operations',
    careerLevel: 'Manager', department: 'Operations',
    purpose: 'Runs daily operations so the company delivers reliably.',
    summary: 'Plans work, manages staff and improves how operations run.',
    duties: ['Plan and assign daily work', 'Monitor service levels and resolve blockers', 'Manage hiring, scheduling and performance for the team'],
    responsibilities: ['Maintain operating procedures', 'Report operating results to leadership'],
    outcomes: ['Service commitments are met', 'The team is staffed and trained'],
    reportsTo: 'Chief Executive Officer', education: 'Bachelor’s degree or equivalent experience', experience: '5 years in operations including team leadership',
    skills: ['Scheduling', 'Process improvement'], professionalSkills: ['Leadership', 'Decision making'],
    payType: 'Salary', overtimeEligible: null, bonus: true,
  },
  {
    slug: 'sales-representative', code: 'SAL-REP', title: 'Sales Representative', socCode: '41-4012', jobFamily: 'Sales',
    careerLevel: 'Intermediate', department: 'Sales',
    purpose: 'Grows the business by winning and keeping customers.',
    summary: 'Finds prospects, presents the company’s offering and manages accounts.',
    duties: ['Contact and qualify prospects', 'Prepare and present proposals', 'Maintain customer records and follow up'],
    responsibilities: ['Forecast pipeline for the sales manager', 'Represent the company professionally'],
    outcomes: ['Pipeline is current and accurate', 'Customers renew and refer'],
    reportsTo: 'Sales Manager', education: 'High school diploma or equivalent', experience: '1 year in sales or customer-facing work',
    skills: ['CRM software', 'Presentations'], professionalSkills: ['Persuasion', 'Listening'],
    payType: 'Salary and commission', overtimeEligible: null, commission: true,
  },
  {
    slug: 'customer-support-specialist', code: 'SUP-SPEC', title: 'Customer Support Specialist', socCode: '43-4051', jobFamily: 'Customer Support',
    careerLevel: 'Entry', department: 'Customer Support',
    purpose: 'Helps customers get answers and resolves their problems.',
    summary: 'Responds to customer requests by phone, email and chat and records each case.',
    duties: ['Answer customer questions and log each contact', 'Troubleshoot issues or escalate them with clear notes', 'Follow up until the customer confirms resolution'],
    responsibilities: ['Maintain knowledge base articles', 'Share trends with the support lead'],
    outcomes: ['Customers receive timely, accurate answers', 'Escalations include what is needed to act'],
    reportsTo: 'Support Lead', education: 'High school diploma or equivalent', experience: 'No prior experience required',
    skills: ['Ticketing software', 'Typing'], professionalSkills: ['Empathy', 'Clear writing'],
    payType: 'Hourly', overtimeEligible: null,
  },
  {
    slug: 'software-engineer', code: 'ENG-SWE', title: 'Software Engineer', socCode: '15-1252', jobFamily: 'Engineering',
    careerLevel: 'Intermediate', department: 'Engineering',
    purpose: 'Builds and maintains the software the company relies on.',
    summary: 'Designs, builds, tests and supports software features with the team.',
    duties: ['Design and implement features with tests', 'Review teammates’ code', 'Diagnose and fix defects'],
    responsibilities: ['Keep documentation current', 'Take part in on-call or support rotations when set by the employer'],
    outcomes: ['Features ship with the agreed quality', 'Defects are fixed in a reasonable time'],
    reportsTo: 'Engineering Manager', education: 'Bachelor’s degree in a technical field or equivalent experience', experience: '2 to 4 years building software',
    skills: ['Programming', 'Testing', 'Version control'], professionalSkills: ['Collaboration', 'Problem solving'],
    payType: 'Salary', overtimeEligible: null,
  },
  {
    slug: 'chief-financial-officer', code: 'EXE-CFO', title: 'Chief Financial Officer', socCode: '11-3031', jobFamily: 'Executive Leadership',
    careerLevel: 'Executive', department: 'Finance',
    purpose: 'Leads the company’s financial strategy, planning and integrity.',
    summary: 'Directs accounting, payroll, treasury and financial planning and advises the executive team.',
    duties: ['Set financial strategy and annual plans with the executive team', 'Oversee accounting, payroll and treasury', 'Present financial results to the board or owners'],
    responsibilities: ['Maintain financial controls and risk management', 'Lead the finance leadership team'],
    outcomes: ['Financial reporting is timely and reliable', 'Plans are funded and tracked'],
    reportsTo: 'Chief Executive Officer', education: 'Bachelor’s degree in finance or accounting; advanced degree or certification preferred', experience: '10 or more years in finance including executive leadership',
    skills: ['Financial planning', 'Internal controls'], professionalSkills: ['Strategic thinking', 'Executive communication'],
    payType: 'Salary', overtimeEligible: null, bonus: true,
  },
];

export function buildMockTemplates(): JobRoleTemplateDetail[] {
  return SEEDS.map((s) => ({
    id: `tpl-${s.slug}`,
    code: s.code,
    title: s.title,
    socCode: s.socCode,
    onetCode: null,
    jobFamily: s.jobFamily,
    careerLevel: s.careerLevel,
    suggestedDepartmentName: s.department,
    templateVersion: 1,
    content: build(s),
  }));
}
